import { h } from "../shared/safe";
import { emptyBlock, errorBanner, pill } from "../shared/blocks";
import { peekIntent, publishScope } from "../shared/intent";
import { actionButton, noticeBlock, section } from "./bits";
import type { Ctx } from "./context";
import { editorialStatus, type MasterView } from "./model";
import { approveOutcomeBlock, deliveryPanel, publishOutcomeBlock } from "./publishBlocks";

// Content: generate an article, then approve it, then publish it. They are three separate steps with three separate
// results and the page never blurs them: generating makes a draft, approving is an editorial decision only, and
// publishing ASKS for delivery (the server answers "queued"), after which the Delivery view shows what destinations
// actually confirmed. A publish response alone never turns an article into "Published".

export interface GenerateForm {
  el: HTMLElement;
  /** Updates the button, field and message to match the view state without rebuilding (and so without losing the caret). */
  sync(): void;
}

/** Built once per page visit. Only `sync()` touches it afterwards, so typing a topic is never interrupted by a repaint. */
export function createGenerateForm(ctx: Ctx): GenerateForm {
  const { state, actions } = ctx;
  const status = h("div", { id: "cd-gen-status", "aria-live": "polite", class: "qf-cd-status" });
  const input = h("input", {
    id: "cd-gen-topic",
    type: "text",
    autocomplete: "off",
    placeholder: "e.g. Paint protection film for trucks in Wilmington",
    value: state.topic,
    "aria-describedby": "cd-gen-status",
    dataset: { focus: "gen-topic" },
    on: { input: (ev) => void (state.topic = (ev.target as HTMLInputElement).value) },
  });
  const button = h("button", { type: "submit", dataset: { focus: "generate" } }, "Generate article");
  const form = h(
    "form",
    {
      class: "grid cols-2 qf-cd-generate",
      novalidate: true,
      on: {
        submit: (ev) => {
          ev.preventDefault();
          if (state.pending.has("generate")) return;
          void actions.generate(state.topic);
        },
      },
    },
    h("div", null, h("label", { for: "cd-gen-topic" }, "Topic for a new article"), input),
    button,
  );
  const sync = () => {
    const busy = state.pending.has("generate");
    button.textContent = busy ? "Generating…" : "Generate article";
    button.classList.toggle("is-pending", busy);
    if (busy) {
      button.setAttribute("aria-busy", "true");
      button.setAttribute("aria-disabled", "true");
    } else {
      button.removeAttribute("aria-busy");
      button.removeAttribute("aria-disabled");
    }
    input.readOnly = busy;
    status.replaceChildren();
    const n = noticeBlock(state.generateNotice);
    if (n) status.append(n);
  };
  sync();
  return { el: h("div", null, form, status), sync };
}

/** True when this article is already known to have an accepted (or automatically accepted) submission. */
function knownSubmitted(ctx: Ctx, id: string, from: "list" | "preview"): boolean {
  const { state } = ctx;
  if (state.autoAccepted.has(id)) return true;
  if ((from === "preview" ? state.generatedPublish : state.publishes.get(id))?.kind === "accepted") return true;
  const d = state.delivery.get(id)?.delivery;
  return !!d && d.state !== "not_submitted";
}

/** Explains the two buttons shown while an earlier publish attempt has no confirmed result. Rendered under the row, not inside the button group. */
function publishHint(ctx: Ctx, id: string, status: string, from: "list" | "preview"): HTMLElement | null {
  if (status !== "approved" || !peekIntent(publishScope(ctx.clientId, id))) return null;
  return h("p", { class: "muted qf-cd-same", id: `same-${from}-${id}`, dataset: { sameRequest: "true" } }, "An earlier publish attempt for this article has no confirmed result. Retry repeats the same request, so it cannot queue the article twice. A new submission is a deliberate second publication.");
}

/**
 * Approve / Publish controls for one article. Publish has four honest forms:
 *  - "Publish": nothing has been submitted for it;
 *  - "Publish again": a submission is already accepted (manually, or by automatic mode) and this starts ANOTHER one;
 *  - "Retry same publish" + "Publish as a new submission": an earlier attempt ended without a readable result. The first
 *    repeats the SAME request (same Idempotency-Key, so it cannot queue twice), the second is a deliberate new one;
 *  - a disabled "Checking delivery…" while the page finds out whether something was already submitted.
 */
function approveOrPublish(ctx: Ctx, m: { id: string; status: string }, from: "list" | "preview"): HTMLElement[] {
  // The pending key is per article, not per place, so Approve/Publish in the preview and in the list share one guard.
  if (m.status === "waiting_review") return [actionButton({ ctx, key: `approve:${m.id}`, focusKey: `${from}-approve:${m.id}`, label: "Approve", busyLabel: "Approving…", onClick: () => ctx.actions.approve(m.id, from) })];
  if (m.status !== "approved" && m.status !== "published") return [];
  const unresolved = peekIntent(publishScope(ctx.clientId, m.id));
  const entry = ctx.state.delivery.get(m.id);
  if (unresolved) {
    return [
      actionButton({ ctx, key: `publish:${m.id}`, focusKey: `${from}-publish:${m.id}`, label: "Retry same publish", busyLabel: "Submitting…", onClick: () => ctx.actions.publish(m.id, from, "default"), describedBy: `same-${from}-${m.id}` }),
      actionButton({ ctx, key: `publish:${m.id}`, focusKey: `${from}-publish-new:${m.id}`, label: "Publish as a new submission", busyLabel: "Submitting…", variant: "ghost", onClick: () => ctx.actions.publish(m.id, from, "new") }),
    ];
  }
  if (entry?.state === "loading" && entry.quiet && !knownSubmitted(ctx, m.id, from)) {
    return [actionButton({ ctx, key: `publish:${m.id}`, focusKey: `${from}-publish:${m.id}`, label: "Checking delivery…", busyLabel: "Checking delivery…", disabled: true, why: "Checking whether this article was already submitted", onClick: () => undefined })];
  }
  // A second publish request queues a second set of delivery jobs, so once one has been accepted the button says so.
  const again = knownSubmitted(ctx, m.id, from);
  return [actionButton({ ctx, key: `publish:${m.id}`, focusKey: `${from}-publish:${m.id}`, label: m.status === "published" ? "Republish article" : again ? "Publish again" : "Publish", busyLabel: "Submitting…", onClick: () => ctx.actions.publish(m.id, from, "default") })];
}

function renderPreview(ctx: Ctx): HTMLElement | null {
  const g = ctx.state.generated;
  if (!g) return null;
  const ed = editorialStatus(g.status);
  const auto = g.autoSubmission;
  return h(
    "div",
    { class: "card qf-cd-preview", dataset: { masterId: g.masterId } },
    h("h3", { class: "qf-cd-preview__title" }, g.title),
    h("p", { class: "muted" }, `Model: ${g.model} · `, pill(ed.label, ed.tone)),
    ctx.state.generatedLine ? h("p", { class: "muted", dataset: { line: "true" } }, ctx.state.generatedLine) : null,
    h("div", { class: "qf-cd-body", tabindex: "0", role: "region", "aria-label": "Generated article text" }, g.body || "(empty)"),
    g.constraintsPassed
      ? h("p", { class: "qf-cd-ok" }, "All constraint checks passed.")
      : h(
          "div",
          { class: "error-banner qf-cd-constraints", role: "alert" },
          h("strong", null, "This draft fails the content rules and cannot be approved."),
          g.errors.length ? h("ul", { class: "qf-cd-notice__list" }, ...g.errors.map((e) => h("li", null, e))) : h("p", null, "The server did not say which rule failed."),
        ),
    g.warnings.length ? h("ul", { class: "qf-cd-warnings" }, ...g.warnings.map((w) => h("li", null, w))) : null,
    h("div", { class: "btn-group" }, g.constraintsPassed || g.status === "approved" ? approveOrPublish(ctx, { id: g.masterId, status: g.status }, "preview") : null),
    publishHint(ctx, g.masterId, g.status, "preview"),
    h("div", { "aria-live": "polite" }, approveOutcomeBlock(ctx.state.generatedApproval), publishOutcomeBlock(ctx.state.generatedPublish)),
  );
}

function masterRow(ctx: Ctx, m: MasterView): HTMLElement {
  const { state } = ctx;
  const ed = editorialStatus(m.status);
  const open = state.deliveryOpen.has(m.id);
  const showDelivery = m.status === "approved" || m.status === "published" || m.status === "failed";
  const actions = h(
    "div",
    { class: "btn-group qf-cd-article__actions" },
    approveOrPublish(ctx, m, "list"),
    showDelivery
      ? h(
          "button",
          {
            type: "button",
            class: "ghost",
            "aria-expanded": open ? "true" : "false",
            dataset: { focus: `delivery:${m.id}` },
            on: { click: () => void ctx.actions.toggleDelivery(m.id) },
          },
          open ? "Hide delivery" : "Delivery",
        )
      : null,
  );
  const approval = approveOutcomeBlock(state.approvals.get(m.id));
  const publish = publishOutcomeBlock(state.publishes.get(m.id));
  const delivery = open ? deliveryPanel(ctx, m.id, state.delivery.get(m.id)) : null;
  // A list, not a table: each article's results and delivery record sit directly under it and wrap at any width.
  return h(
    "li",
    { class: "qf-cd-article", dataset: { masterRow: m.id } },
    h(
      "div",
      { class: "qf-cd-article__row" },
      h("span", { class: "qf-cd-article__title" }, m.title),
      h("span", { class: "qf-cd-article__status" }, pill(ed.label, ed.tone)),
      h("span", { class: "qf-cd-article__model muted" }, m.model ?? ""),
      actions,
    ),
    publishHint(ctx, m.id, m.status, "list"),
    approval || publish ? h("div", { "aria-live": "polite" }, approval, publish) : null,
    delivery,
  );
}

export function renderContentResults(ctx: Ctx): HTMLElement {
  const { data, actions } = ctx;
  const box = h("div", { id: "cd-content-results" });
  box.append(...[renderPreview(ctx)].filter((x): x is HTMLElement => x !== null));
  if (data.masters === null) {
    box.append(errorBanner(`Articles could not be loaded${data.failures.masters ? `: ${data.failures.masters.message}` : ""}.`, { retry: () => void actions.reloadSection("masters") }));
    return box;
  }
  if (data.masters.length === 0) {
    box.append(emptyBlock("No articles yet", "Generate one above."));
    return box;
  }
  box.append(h("ul", { class: "qf-cd-articles", "aria-label": "Articles" }, ...data.masters.map((m) => masterRow(ctx, m))));
  return box;
}

export function contentShell(form: GenerateForm): { el: HTMLElement; results: HTMLElement } {
  const results = h("div", { id: "cd-content-results-slot" });
  return { el: section("cd-content", "Content", form.el, results), results };
}
